import './CreatorSetSnapshot.scss';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import {
  CreatorSetTopVideo,
  YouTubeCreatorSetMember
} from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { SearchField } from '@Components/SearchField/SearchField';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { BPMTable, CellRenderer, Header, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { ACTIVE_SHORTS_BADGE_LABEL } from '../Discover/creator-discovery-constants';
import { formatCompactCount } from '../Discover/creator-discovery-utils';
import { PAGE_TITLE } from '../youtube-planning-constants';
import { percent } from './creator-set-attributes-utils';
import { videoUrl } from './creator-set-export-utils';
import {
  ALL_INTEREST_TYPES,
  BROAD_INTERESTS_TOOLTIP,
  DEMOGRAPHIC_DIMENSIONS,
  DEMOGRAPHIC_LABELS,
  DEMOGRAPHIC_SHARE_NOTES,
  INTEREST_SHARES_NOTE,
  INTEREST_SORT_OPTIONS,
  INTEREST_TYPE_LABELS,
  INTEREST_TYPE_OPTIONS,
  InterestDimension,
  InterestSort,
  InterestSortValue,
  InterestTypeFilter,
  NO_VIEWER_DATA,
  SNAPSHOT_VIEW_OPTIONS,
  SnapshotView,
  SnapshotViewValue
} from './creator-sets-constants';
import {
  comparedMembers,
  CreatorSummary,
  DemographicRow,
  filterInterestRows,
  InterestCell,
  InterestRow,
  missingInterestTooltip,
  shareLevelClass,
  standoutText,
  toCreatorSummaries,
  toDemographicRows,
  toInterestRows
} from './creator-set-snapshot-utils';
import { formatSetDate, parseCount } from './creator-sets-utils';

const INTEREST_PAGE_SIZE = 25;
const ROW_HEIGHT = 40;
const SHARE_COLUMN_WIDTH = 180;
const BAR_COLUMN_WIDTH = 200;
const GROUP_COLUMN_WIDTH = 120;

const renderNoInterests = () => <TableEmptyState message='No interests match.' />;

interface CreatorSetSnapshotProps {
  members: YouTubeCreatorSetMember[];
}

// A side-by-side read of the set's saved snapshots: what each creator's audience stands out for,
// their demographics and their top videos.
export const CreatorSetSnapshot = ({ members }: CreatorSetSnapshotProps) => {
  const [view, setView] = useState<SnapshotViewValue>(SnapshotView.INTERESTS);
  const compared = useMemo(() => comparedMembers(members), [members]);
  const interestRows = useMemo(() => toInterestRows(compared), [compared]);
  const summaries = useMemo(
    () => toCreatorSummaries(members, interestRows),
    [members, interestRows]
  );

  if (members.length === 0) return undefined;

  const onViewChange = (next: SnapshotViewValue) => {
    setView(next);
    Mixpanel.track(MxE.TabChange, { page: PAGE_TITLE, option_selected: next });
  };

  return (
    <section className='creator-set-snapshot' data-testid='creator-set-snapshot'>
      <div className='creator-set-snapshot-header'>
        <div className='creator-set-snapshot-title'>Compare creators</div>
        <div className='creator-set-snapshot-note'>
          From each creator’s saved metrics. Audience details come from{' '}
          {conditionallyPluralizeWithCount(compared.length, 'creator')} with viewer data.
        </div>
      </div>
      <div className='creator-summaries'>
        {summaries.map(summary => (
          <CreatorSummaryCard
            key={summary.channelId}
            summary={summary}
            canCompare={compared.length > 1}
          />
        ))}
      </div>
      <SegmentedControlButton
        options={SNAPSHOT_VIEW_OPTIONS}
        selectedOption={view}
        size={Size.SM}
        onChange={onViewChange}
      />
      {view === SnapshotView.INTERESTS && <InterestsView rows={interestRows} compared={compared} />}
      {view === SnapshotView.DEMOGRAPHICS && <DemographicsView compared={compared} />}
      {view === SnapshotView.TOP_VIDEOS && <TopVideosView members={members} />}
    </section>
  );
};

interface CreatorSummaryCardProps {
  summary: CreatorSummary;
  // False when fewer than two creators have viewer data, so nobody can lead an interest.
  canCompare: boolean;
}

const CreatorSummaryCard = ({ summary, canCompare }: CreatorSummaryCardProps) => (
  <div className='creator-summary' data-testid={`creator-summary-${summary.channelId}`}>
    <div className='creator-summary-name'>
      {summary.thumbnailUrl && (
        <img className='creator-summary-thumbnail' src={summary.thumbnailUrl} alt='' />
      )}
      <span>{summary.name}</span>
    </div>
    <dl className='creator-summary-stats'>
      <Stat label='Subscribers' value={formatCompactCount(summary.subscribers)} />
      <Stat label='Total views' value={formatCompactCount(summary.totalViews)} />
      <Stat
        label='Median top-video views'
        value={formatCompactCount(summary.medianTopVideoViews)}
      />
      <Stat label='Newest top video' value={formatSetDate(summary.newestTopVideo)} />
    </dl>
    <div className='creator-summary-standouts'>
      <span className='creator-summary-label'>Audience stands out for</span>
      <span>{standoutText(summary, canCompare)}</span>
    </div>
    <div className='creator-summary-badges'>
      {summary.partnerships.map(label => (
        <Badge key={label} size={Size.SM} appearance={Appearance.OUTLINED}>
          {label}
        </Badge>
      ))}
      {summary.isActiveShortsCreator && (
        <Badge size={Size.SM} appearance={Appearance.OUTLINED} design={Design.NEUTRAL}>
          {ACTIVE_SHORTS_BADGE_LABEL}
        </Badge>
      )}
    </div>
  </div>
);

interface StatProps {
  label: string;
  value: string;
}

const Stat = ({ label, value }: StatProps) => (
  <div className='creator-summary-stat'>
    <dt className='creator-summary-label'>{label}</dt>
    <dd>{value}</dd>
  </div>
);

interface InterestsViewProps {
  rows: InterestRow[];
  compared: YouTubeCreatorSetMember[];
}

const InterestsView = ({ rows, compared }: InterestsViewProps) => {
  const [type, setType] = useState<InterestTypeFilter>(ALL_INTEREST_TYPES);
  const [query, setQuery] = useState('');
  const [hideBroad, setHideBroad] = useState(true);
  const [sort, setSort] = useState<InterestSortValue>(
    compared.length > 1 ? InterestSort.DISTINCTIVE : InterestSort.SHARE
  );
  const [clickedChannelId, setClickedChannelId] = useState<string>();
  const [showAll, setShowAll] = useState(false);
  const canCompare = compared.length > 1;
  const sortChannelId = compared.some(({ channel_id }) => channel_id === clickedChannelId)
    ? clickedChannelId
    : undefined;

  const visible = useMemo(
    () => filterInterestRows(rows, { type, query, hideBroad, sort, sortChannelId }),
    [rows, type, query, hideBroad, sort, sortChannelId]
  );
  const shown = showAll ? visible : visible.slice(0, INTEREST_PAGE_SIZE);

  const handleSortChange = (key: string) => {
    const option = INTEREST_SORT_OPTIONS.find(({ key: value }) => value === key);
    if (!option) return;
    setSort(option.key);
    setClickedChannelId(undefined);
  };

  const renderInterestHeader = useCallback<CellRenderer<Header>>(
    ({ data: label, columnIndex }) => {
      const channelId = compared[columnIndex - 1]?.channel_id;
      if (!channelId || !canCompare) return <span>{String(label)}</span>;
      const isSorted = channelId === sortChannelId;
      return (
        <InteractiveSpan
          className={cn('use-sort-header-caret', { down: isSorted })}
          title={`Sort by ${String(label)}’s share`}
          aria-pressed={isSorted}
          onClick={() => setClickedChannelId(isSorted ? undefined : channelId)}
        >
          {String(label)}
        </InteractiveSpan>
      );
    },
    [compared, canCompare, sortChannelId]
  );

  const interestHeaders = useMemo<TypedHeader<InterestRow>[]>(
    () => [
      {
        name: 'interest',
        label: 'Interest',
        flex: 1,
        minFlexWidth: 320,
        nonInteractive: true,
        renderer: row => <InterestLabel row={row} />
      },
      ...compared.map(({ channel_id, insights }): TypedHeader<InterestRow> => ({
        name: channel_id,
        label: insights.display_name,
        width: SHARE_COLUMN_WIDTH,
        nonInteractive: true,
        renderer: row => (
          <ShareCell
            cell={row.cells[channel_id]}
            dimension={row.dimension}
            isLeader={row.leaderId === channel_id}
          />
        )
      }))
    ],
    [compared]
  );

  if (compared.length === 0)
    return <div className='creator-set-snapshot-empty'>{NO_VIEWER_DATA}</div>;

  return (
    <div className='snapshot-view' data-testid='snapshot-interests'>
      <div className='snapshot-toolbar'>
        <SegmentedControlButton
          options={INTEREST_TYPE_OPTIONS}
          selectedOption={type}
          size={Size.SM}
          onChange={setType}
        />
        <SearchField
          className='snapshot-search'
          size={Size.SM}
          value={query}
          placeholder='Find an interest, e.g. fitness'
          aria-label='Find an interest'
          onChange={e => setQuery(e.target.value)}
        />
        <SegmentedControlButton
          options={INTEREST_SORT_OPTIONS}
          selectedOption={sortChannelId ?? sort}
          size={Size.SM}
          design={Design.NEUTRAL}
          disabled={!canCompare}
          onChange={handleSortChange}
        />
        <ToggleSwitch
          label='Hide broad interests'
          size={Size.SM}
          disabled={!canCompare}
          checked={hideBroad}
          onChange={setHideBroad}
          infoTooltipText={BROAD_INTERESTS_TOOLTIP}
        />
      </div>
      <div className='creator-set-snapshot-note'>
        {INTEREST_SHARES_NOTE} Showing {shown.length} of{' '}
        {conditionallyPluralizeWithCount(visible.length, 'interest')}.
      </div>
      <div className='snapshot-table'>
        <BPMTable
          autoHeight
          data={shown}
          headers={interestHeaders}
          headersRenderer={renderInterestHeader}
          filterBar={false}
          rowHeight={ROW_HEIGHT}
          noRowsRenderer={renderNoInterests}
        />
      </div>
      {visible.length > INTEREST_PAGE_SIZE && (
        <Button
          size={Size.SM}
          appearance={Appearance.OUTLINED}
          className='snapshot-show-all'
          onClick={() => setShowAll(current => !current)}
        >
          {showAll ? 'Show fewer' : `Show all ${visible.length}`}
        </Button>
      )}
    </div>
  );
};

interface InterestLabelProps {
  row: InterestRow;
}

const InterestLabel = ({ row }: InterestLabelProps) => (
  <span className='interest-label'>
    {row.label}
    <Badge size={Size.SM} design={Design.NEUTRAL} appearance={Appearance.OUTLINED}>
      {INTEREST_TYPE_LABELS[row.dimension]}
    </Badge>
    {row.isBroad && (
      <Badge size={Size.SM} design={Intent.INFO}>
        Broad
      </Badge>
    )}
  </span>
);

interface ShareCellProps {
  cell: InterestCell;
  dimension: InterestDimension;
  isLeader: boolean;
}

const ShareCell = ({ cell: { share, ceiling }, dimension, isLeader }: ShareCellProps) => {
  if (share === undefined) {
    return (
      <div className='share-cell missing' title={missingInterestTooltip(dimension, ceiling)}>
        —
      </div>
    );
  }
  return (
    <div className={cn('share-cell', shareLevelClass(share), { leader: isLeader })}>
      {percent(share)}
    </div>
  );
};

interface DemographicsViewProps {
  // The members who share viewer data.
  compared: YouTubeCreatorSetMember[];
}

const DemographicsView = ({ compared }: DemographicsViewProps) => {
  const rows = useMemo(() => toDemographicRows(compared), [compared]);
  const headers = useMemo<TypedHeader<DemographicRow>[]>(
    () => [
      {
        name: 'group',
        label: 'Group',
        width: GROUP_COLUMN_WIDTH,
        nonInteractive: true,
        renderer: (row, index) => (
          <span className='demographic-label'>
            {rows[index - 1]?.dimension === row.dimension ? '' : DEMOGRAPHIC_LABELS[row.dimension]}
          </span>
        )
      },
      {
        name: 'detail',
        label: 'Detail',
        flex: 1,
        nonInteractive: true,
        renderer: row => <span className='demographic-label'>{row.label}</span>
      },
      ...compared.map(({ channel_id, insights }): TypedHeader<DemographicRow> => ({
        name: channel_id,
        label: insights.display_name,
        width: BAR_COLUMN_WIDTH,
        nonInteractive: true,
        renderer: row => <BarCell share={row.shares[channel_id]} />
      }))
    ],
    [rows, compared]
  );

  if (compared.length === 0)
    return <div className='creator-set-snapshot-empty'>{NO_VIEWER_DATA}</div>;

  return (
    <div className='snapshot-view' data-testid='snapshot-demographics'>
      <div className='creator-set-snapshot-note'>
        Percent of each creator’s viewers in each group. “—” means the group isn’t in that creator’s
        data.
        <ul className='demographic-notes'>
          {DEMOGRAPHIC_DIMENSIONS.map(dimension => (
            <li key={dimension}>
              <span className='demographic-notes-group'>{DEMOGRAPHIC_LABELS[dimension]}:</span>{' '}
              {DEMOGRAPHIC_SHARE_NOTES[dimension]}
            </li>
          ))}
        </ul>
      </div>
      <div className='snapshot-table'>
        <BPMTable
          autoHeight
          data={rows}
          headers={headers}
          filterBar={false}
          rowHeight={ROW_HEIGHT}
        />
      </div>
    </div>
  );
};

interface BarCellProps {
  share?: number;
}

const BarCell = ({ share }: BarCellProps) => (
  <div className='bar-cell'>
    {share === undefined ? (
      '—'
    ) : (
      <span className='bar'>
        <span className='bar-fill' style={{ width: percent(share) }} />
        <span className='bar-value'>{percent(share)}</span>
      </span>
    )}
  </div>
);

interface TopVideosViewProps {
  members: YouTubeCreatorSetMember[];
}

const TopVideosView = ({ members }: TopVideosViewProps) => (
  <div className='snapshot-view top-videos' data-testid='snapshot-top-videos'>
    {members.map(({ channel_id, insights }) => (
      <TopVideosColumn
        key={channel_id}
        name={insights.display_name}
        videos={insights.top_videos ?? []}
      />
    ))}
  </div>
);

interface TopVideosColumnProps {
  name: string;
  videos: CreatorSetTopVideo[];
}

const TopVideosColumn = ({ name, videos }: TopVideosColumnProps) => (
  <div className='top-videos-column'>
    <div className='top-videos-creator'>{name}</div>
    {videos.length === 0 && (
      <div className='creator-set-snapshot-note'>
        No top videos saved. Refresh metrics to add them.
      </div>
    )}
    {videos.map((video, index) => (
      <TopVideo key={`${video.video_id}-${index}`} video={video} />
    ))}
  </div>
);

interface TopVideoLinkProps {
  video: CreatorSetTopVideo;
}

const TopVideo = ({ video }: TopVideoLinkProps) => {
  const content = (
    <>
      {video.thumbnail_url && (
        <img className='top-video-thumbnail' src={video.thumbnail_url} alt='' />
      )}
      <span className='top-video-text'>
        <span className='top-video-title'>{video.title}</span>
        <span className='creator-set-snapshot-note'>
          {formatCompactCount(parseCount(video.views_count))} views ·{' '}
          {formatSetDate(video.publish_date)}
        </span>
      </span>
    </>
  );
  if (!video.video_id) return <div className='top-video'>{content}</div>;
  return (
    <a className='top-video' href={videoUrl(video.video_id)} target='_blank' rel='noreferrer'>
      {content}
    </a>
  );
};
